import { Box, Chip } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useInventoryDestinationFields } from "../../../../hooks"
import type { AddPalletsAdjustmentFormValues } from "../../schemas"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { BIN_SIZE_OPTIONS } from "@/constants/warehouse"
import type { WarehouseArea } from "@/graphql/codegen/graphql"

export interface AddPalletsAdjustmentDestinationFieldSetProps {
  index: number
  warehouseArea?: WarehouseArea | null
  selectedShelvingIds: string[]
}

export const AddPalletsAdjustmentDestinationFieldSet: FC<AddPalletsAdjustmentDestinationFieldSetProps> = ({
  index,
  warehouseArea,
  selectedShelvingIds,
}) => {
  const { control, watch, setValue } = useFormContext<AddPalletsAdjustmentFormValues>()

  const {
    fieldPaths,
    aisleCode,
    sectionCode,
    shouldShowShelvingFields,
    holdAreaOptions,
    aisleOptions,
    shelfOptions,
    binOptions,
    isFetching,
    isAisleFull,
    isSectionFull,
    handleHoldAreaChange,
    handleBinSizeChange,
    handleAisleCodeChange,
    handleSectionCodeChange,
    handleBinNumberChange,
  } = useInventoryDestinationFields<AddPalletsAdjustmentFormValues>({
    name: `destinations.${index}`,
    watch,
    setValue,
    warehouseArea,
    selectedShelvingIds,
  })

  return (
    <Box className='flex gap-4'>
      <Chip
        label={`Pallet ${index + 1}`}
        size='small'
        className='mt-8 w-[100px] shrink-0 rounded-full bg-gray-300 text-gray-900'
      />
      <FormSelect
        control={control}
        name={fieldPaths.holdArea}
        label='Area'
        required
        placeholder='Select one'
        options={holdAreaOptions}
        onChange={handleHoldAreaChange}
        data-testid={`inventory.add-pallets-adjustment-destination-field-set.area-field-${index}`}
      />
      {shouldShowShelvingFields && (
        <>
          <FormAutocomplete
            control={control}
            name={fieldPaths.binSize}
            label='Bin Size'
            placeholder='Select one'
            options={BIN_SIZE_OPTIONS}
            onChange={handleBinSizeChange}
            data-testid={`inventory.add-pallets-adjustment-destination-field-set.bin-size-field-${index}`}
          />
          <FormAutocomplete
            control={control}
            name={fieldPaths.aisleCode}
            label='Aisle'
            required
            placeholder='Select one'
            options={aisleOptions}
            disabled={isFetching}
            noOptionsText='No aisles'
            error={isAisleFull}
            helperText={isAisleFull ? "No available bins in this aisle" : undefined}
            onChange={handleAisleCodeChange}
            data-testid={`inventory.add-pallets-adjustment-destination-field-set.aisle-field-${index}`}
          />
          <FormAutocomplete
            control={control}
            name={fieldPaths.sectionCode}
            label='Shelf'
            required
            placeholder='Select one'
            options={shelfOptions}
            disabled={!aisleCode || isFetching}
            noOptionsText='No shelves'
            error={isSectionFull}
            helperText={isSectionFull ? "No available bins on this shelf" : undefined}
            onChange={handleSectionCodeChange}
            data-testid={`inventory.add-pallets-adjustment-destination-field-set.shelf-field-${index}`}
          />
          <FormAutocomplete
            control={control}
            name={fieldPaths.binNumber}
            label='Bin'
            required
            placeholder='Select one'
            options={binOptions}
            disabled={!sectionCode || isFetching}
            noOptionsText='No bins'
            onChange={handleBinNumberChange}
            data-testid={`inventory.add-pallets-adjustment-destination-field-set.bin-field-${index}`}
          />
        </>
      )}
    </Box>
  )
}
